import { Link as RouterLink } from 'react-router-dom';
import { isMetricDeltaVisible, isMetricVisible } from '@/features/shared/metricVisibility';
import { resolveEntityTags } from '@/features/shared/tags';
import { uiTokens } from '@/theme/uiTokens';
import { Box, formatStatusLabel, getStatusTextColor, Link, Text, Tooltip } from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { StackedCountBar, TableTextCell, TagsInlineCell } from '@/ui/composites/table';
import { buildMetricColumns, type MetricColumnKey } from '@/ui/composites/table/tableUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { MetricThresholdGroup, SuiteExecutionStatus, SuiteListItemDto } from '@flowqa/shared';

type SuitesColumnsOptions = {
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string) => void;
  onStatusSelect?: (status: SuiteExecutionStatus | null | undefined) => void;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  showDeltas?: boolean;
  disableLinks?: boolean;
};

const columnFlex = {
  name: 1,
  latest: 0.5,
  lastExecuted: 0.5,
  latestTests: 1,
  passRate: 0.5,
  frequency: 0.5,
  avgDuration: 0.5,
  flakeScore: 0.5,
  mttf: 0.5,
  delta: 0.4,
  tags: 0.75,
};
const RELATIVE_TIME_TEXT_SX = {
  display: 'block',
  width: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const buildSuitesColumns = ({
  selectedProjectId,
  onProjectSelect,
  onStatusSelect,
  thresholds,
  resolveThresholds,
  showDeltas = true,
  disableLinks = false,
}: SuitesColumnsOptions): Array<GridColDef<SuiteListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: SuiteListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };
  const metricKeys: MetricColumnKey[] = [
    'passRate',
    'frequency',
    'avgDuration',
    ...(isMetricVisible('flakeScore') ? (['flakeScore'] as const) : []),
    ...(isMetricVisible('mttf') ? (['mttf'] as const) : []),
  ];

  const nameColumn: GridColDef<SuiteListItemDto> = {
    field: 'name',
    headerName: 'Suite',
    flex: columnFlex.name,
    align: 'left',
    headerAlign: 'left',
    filterOperators: stringOperators,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => (
      <TableTextCell
        primary={{
          label: params.row.name,
          ...(disableLinks
            ? {}
            : {
                href: `/suites/${params.row.slug}`,
                onClick: () => {
                  onProjectSelect(params.row.projectId);
                },
              }),
        }}
      />
    ),
  };

  const latestSummaryColumn: GridColDef<SuiteListItemDto> = {
    field: 'latestExecutionStatus',
    headerName: 'Lastest Status',
    flex: columnFlex.latest,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => {
      const latestExecution = params.row.latestExecution;
      const status = latestExecution?.status ?? null;
      const isClickable = Boolean(status && onStatusSelect);
      const label = status ? formatStatusLabel(status) : 'Untested';
      const runNumber = latestExecution?.runNumber ?? null;
      const executionHref =
        runNumber !== null && params.row.slug
          ? `/suites/${params.row.slug}/runs/${runNumber}`
          : null;
      const labelNode =
        executionHref && !disableLinks ? (
          <Link
            component={RouterLink}
            to={executionHref}
            underline="hover"
            color="inherit"
            onClick={(event) => {
              event.stopPropagation();
              onProjectSelect(params.row.projectId);
            }}
          >
            <Text role="body" component="span" color="inherit">
              {label}
            </Text>
          </Link>
        ) : (
          label
        );
      if (isClickable && !executionHref) {
        return (
          <Box
            component="span"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              onStatusSelect?.(status ?? null);
            }}
          >
            <Text
              role="body"
              component="span"
              sx={{
                color: (theme) => getStatusTextColor(theme, status),
              }}
              transform="uppercase"
            >
              {labelNode}
            </Text>
          </Box>
        );
      }
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      );
    },
  };

  const lastExecutedColumn: GridColDef<SuiteListItemDto, Date | string | null> = {
    field: 'latestExecutionTimestamp',
    headerName: 'Last Executed',
    flex: columnFlex.lastExecuted,
    align: 'right',
    headerAlign: 'right',
    filterable: false,
    valueGetter: (_value, row) => row.latestExecution?.timestamp ?? null,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto, Date | string | null>) => {
      const timestamp = params.value ?? null;
      const label = timestamp ? formatRelativeTime(timestamp) : '-';
      const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      return (
        <Tooltip
          title={fullTimestamp}
          disableHoverListener={!fullTimestamp}
          arrow
          slotProps={{
            tooltip: { sx: { fontSize: (theme) => theme.typography.body2.fontSize } },
          }}
        >
          <Box component="span" sx={{ display: 'block', width: '100%' }} title={label}>
            <Text role="body" component="span" sx={RELATIVE_TIME_TEXT_SX}>
              {label.length > 0 ? label : '-'}
            </Text>
          </Box>
        </Tooltip>
      );
    },
  };

  const latestTestsColumn: GridColDef<SuiteListItemDto> = {
    field: 'testLatestExecutionPassed',
    headerName: 'Latest Tests',
    flex: columnFlex.latestTests,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = params.row.latestTestResults?.counts;
      const passed = counts?.passed ?? 0;
      const failed = counts?.failed ?? 0;
      const errored = counts?.errored ?? 0;
      const skipped = counts?.skipped ?? 0;
      const untested = counts?.untested ?? 0;
      const total = passed + failed + errored + skipped + untested;
      const segments = [
        { label: 'Passed', value: passed, color: uiTokens.statusColors.green },
        { label: 'Failed', value: failed, color: uiTokens.statusColors.red },
        { label: 'Errored', value: errored, color: uiTokens.statusColors.brown },
        { label: 'Skipped', value: skipped, color: uiTokens.statusColors.orange },
        { label: 'Untested', value: untested, color: uiTokens.statusColors.neutral },
      ];
      return <StackedCountBar segments={segments} total={total} totalLabel={`Tests: ${total}`} />;
    },
  };

  const metricColumns = buildMetricColumns<SuiteListItemDto>({
    enabledKeys: metricKeys,
    includeDeltas: (key) => {
      if (!deltasEnabled) return false;
      if (key === 'flakeScore') return isMetricDeltaVisible('flakeScore');
      if (key === 'mttf') return isMetricDeltaVisible('mttf');
      return true;
    },
    fields: {
      passRate: 'metrics.passRate',
      frequency: 'metrics.frequencyPerDay',
      avgDuration: 'metrics.averageDurationMs',
      flakeScore: 'metrics.flakeScore',
      mttf: 'metrics.mttfSeconds',
    },
    deltaFields: {
      passRate: 'metrics.delta.passRate',
      frequency: 'metrics.delta.frequencyPerDay',
      avgDuration: 'metrics.delta.averageDurationMs',
      flakeScore: 'metrics.delta.flakeScore',
      mttf: 'metrics.delta.mttfSeconds',
    },
    flex: {
      passRate: columnFlex.passRate,
      frequency: columnFlex.frequency,
      avgDuration: columnFlex.avgDuration,
      flakeScore: columnFlex.flakeScore,
      mttf: columnFlex.mttf,
    },
    deltaFlex: {
      passRate: columnFlex.delta,
      frequency: columnFlex.delta,
      avgDuration: columnFlex.delta,
      flakeScore: columnFlex.delta,
      mttf: columnFlex.delta,
    },
    thresholds: thresholds,
    resolveThresholds: (row) => resolvedThresholds(row),
    align: 'right',
    headerAlign: 'right',
  });

  const tagsColumn: GridColDef<SuiteListItemDto> = {
    field: 'tags',
    headerName: 'Tags',
    flex: columnFlex.tags,
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => (
      <TagsInlineCell tags={resolveEntityTags(params.row)} />
    ),
  };

  return [
    nameColumn,
    latestSummaryColumn,
    latestTestsColumn,
    lastExecutedColumn,
    ...metricColumns,
    tagsColumn,
  ];
};
